import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector, useStore } from 'react-redux';
import {
  Switch,
  Route,
  Redirect,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api';
import { load as loadRubric } from 'js/actions/rubric-actions';
import { list as listTeams } from 'js/actions/team-actions';
import RubricPageLayout from '../components/RubricPageLayout';
import RubricConfigurationContainer from './RubricConfigurationContainer';
import RubricSettingsContainer from './RubricSettingsContainer';
import RubricPreviewsContainer from './RubricPreviewsContainer';
import RubricWeightsContainer from './RubricWeightsContainer';

function RubricRouter({ token, rubric, loadRubric, listTeams }) {
  const { workspaceId, rubricId } = useParams();
  const match = useRouteMatch();
  const store = useStore();
  const [teamId, setTeamId] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);

  const teamsQueryId = useMemo(
    () => `w/${workspaceId}/rubrics/teams`,
    [workspaceId]
  );

  useEffect(() => {
    setTeamId('');
  }, [workspaceId, rubricId]);

  useEffect(() => {
    setLoadFailed(false);
  }, [rubricId]);

  // const rubric = useSelector((state) => {
  //   const row = state.entities.rubrics[rubricId];
  //   if (!row) return undefined;
  //   return denormalize(row, SCHEMAS.RUBRIC, state.entities);
  // });

  useEffect(() => {
    if (rubric) {
      setLoadFailed(false);
    }
  }, [rubric]);

  useEffect(() => {
    loadRubric(rubricId).catch(() => {
      if (!store.getState().entities.rubrics[rubricId]) {
        setLoadFailed(true);
      }
    });
  }, [loadRubric, rubricId, store]);

  useEffect(() => {
    listTeams({}, teamsQueryId);
  }, [listTeams, teamsQueryId]);

  const teams = useSelector((state) => {
    const { result } = state.queries[teamsQueryId] || {};
    if (!result) return [];
    return denormalize(result, SCHEMAS.TEAM_ARRAY, state.entities).sort(
      (a, b) => (a.name || '').localeCompare(b.name || '')
    );
  });

  useEffect(() => {
    if (teams.length > 0 && teamId === '') {
      setTeamId(String(teams[0].id));
    }
  }, [teams, teamId]);

  const baseUrl = `/w/${workspaceId}/rubrics/${rubricId}`;

  return (
    <RubricPageLayout rubric={rubric} rubricLoadFailed={loadFailed}>
      <Switch>
        <Route
          exact
          path={`${match.path}/settings`}
          render={() => (
            <RubricSettingsContainer
              workspaceId={workspaceId}
              rubricId={rubricId}
              rubric={rubric}
              rubricLoadFailed={loadFailed}
            />
          )}
        />
        <Route
          exact
          path={`${match.path}/previews`}
          render={() => (
            <RubricPreviewsContainer
              rubricId={rubricId}
              rubric={rubric}
              teamId={teamId}
              teams={teams}
              onTeamIdChange={setTeamId}
            />
          )}
        />
        <Route
          exact
          path={`${match.path}/weights`}
          render={() => (
            <RubricWeightsContainer
              workspaceId={workspaceId}
              rubricId={rubricId}
              rubric={rubric}
              rubricLoadFailed={loadFailed}
            />
          )}
        />
        <Route
          exact
          path={match.path}
          render={() => (
            <RubricConfigurationContainer
              workspaceId={workspaceId}
              rubricId={rubricId}
              rubric={rubric}
              rubricLoadFailed={loadFailed}
            />
          )}
        />
        <Redirect to={baseUrl} />
      </Switch>
    </RubricPageLayout>
  );
}

export default connect(
  (state, props) => {
    const {
      match: {
        params: { rubricId },
      },
    } = props;
    const { entities } = state;

    const rubric = denormalize(
      entities.rubrics[rubricId],
      SCHEMAS.RUBRIC,
      entities
    );

    return { token: state.identity.token, rubric };
  },
  {
    loadRubric,
    listTeams,
  }
)(RubricRouter);
